import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ErroApi } from "@/lib/queryClient";
import { dataCurtaBR } from "@shared/boletim-periodo";
import {
  ATALHOS_VENCIMENTO_DIAS, checarVencimento, mensagemDeFalhaDeData, vencimentoDoAtalho,
} from "@shared/mercado";

/**
 * O vencimento que falta para "Emitir e confirmar".
 *
 * Só abre quando o registro do comprador NÃO trouxe prazo — com prazo, o botão
 * emite direto (o prazo que ele combinou vai para a venda). É a mesma exigência
 * de formalizar: nota EM ABERTO precisa de vencimento. Os atalhos contam da DATA
 * DA VENDA, nunca de hoje (ver `ATALHOS_VENCIMENTO_DIAS`).
 */
export function DialogEmitirEConfirmar({
  compradorNome, dataVenda, pendente, erro, onEmitir, onFechar,
}: {
  compradorNome: string;
  dataVenda: string;
  pendente: boolean;
  /** A recusa do servidor, com o motivo — nunca um botão que "não faz nada". */
  erro: string | null;
  onEmitir: (vencimento: string) => void;
  onFechar: () => void;
}) {
  const [vencimento, setVencimento] = useState("");
  const falha = vencimento ? checarVencimento(vencimento, dataVenda) : null;

  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-emitir-e-confirmar">
        <DialogHeader>
          <DialogTitle>Emitir e confirmar com {compradorNome}</DialogTitle>
          <DialogDescription>
            {compradorNome} registrou esta compra sem prazo. Informe o vencimento: ele vai no romaneio que fica
            formalizado com ele.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="vencimento-emitir">Vencimento</Label>
          <div className="flex flex-wrap gap-2">
            {ATALHOS_VENCIMENTO_DIAS.map(dias => {
              const data = vencimentoDoAtalho(dataVenda, dias);
              return (
                <Button
                  key={dias}
                  type="button"
                  size="sm"
                  variant={vencimento === data ? "default" : "outline"}
                  onClick={() => setVencimento(data)}
                  data-testid={`atalho-vencimento-${dias}`}
                >
                  {dias} dias · {dataCurtaBR(data)}
                </Button>
              );
            })}
          </div>
          <Input
            id="vencimento-emitir"
            type="date"
            value={vencimento}
            onChange={e => setVencimento(e.target.value)}
            data-testid="input-vencimento-emitir"
          />
          {falha && <p className="text-xs text-erro">{mensagemDeFalhaDeData(falha, "vencimento")}</p>}
        </div>

        {erro && <p className="text-sm text-destructive" role="alert" data-testid="erro-emitir-e-confirmar">{erro}</p>}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button disabled={!vencimento || !!falha || pendente} onClick={() => onEmitir(vencimento)} data-testid="button-emitir-e-confirmar-dialog">
            {pendente ? "Emitindo…" : `Emitir e confirmar com ${compradorNome}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A mensagem do servidor (qual regra e por quê), via `ErroApi`. */
export function mensagemDeErroDaEmissao(e: Error): string {
  return e instanceof ErroApi && e.mensagem ? e.mensagem : e.message;
}
